import { Core } from "@faire/design-tokens";
import { SlideIn } from "@faire/retailer-visitor-shared/components/SlideIn";
import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/retailer-visitor-shared/lib/popSignUp";
import { getIsRobot } from "@faire/retailer-visitor-shared/serialized-data/getIsRobot";
import { getSessionCountry } from "@faire/retailer-visitor-shared/serialized-data/getSessionCountry";
import { LoggedOutHomeImage } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/__internal__/LoggedOutHomeImage/LoggedOutHomeImage";
import { useGetTopHeaderBannerVariant } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/TopHeaderBanner/__internal__/useGetTopHeaderBannerVariants";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { logAssert } from "@faire/web--source/common/logging";
import { getLocaleKey } from "@faire/web--source/common/serialized-data/getLocaleKey";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Flex } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { cn } from "@faire/web--source/tools/cn";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { trackLoggedOutRetailersLoggedOutHomeRedesignView } from "@faire/web-api--source/events/loggedOutRetailers/view/loggedOutHomeRedesign";
import React from "react";
import { useHistory } from "react-router-dom";

import { useIsLoggedOutHomepageV2 } from "../../useIsLoggedOutHomepageV2";

import {
  MOBILE_BANNER_WIDTH,
  TABLET_BANNER_WIDTH,
  DESKTOP_BANNER_WIDTH,
  XL_DESKTOP_BANNER_WIDTH,
  XXL_DESKTOP_BANNER_WIDTH,
  MOBILE_BANNER_HEIGHT,
  TABLET_BANNER_HEIGHT,
  DESKTOP_BANNER_HEIGHT,
  XL_DESKTOP_BANNER_HEIGHT,
} from "./consts";

export const TopHeaderBanner: React.FC<{
  loadTimingRef?: React.RefCallback<HTMLElement>;
}> = ({ loadTimingRef }) => {
  const countryCode = getSessionCountry();
  const localeCountry = getLocaleKey()?.country;
  const isRobot = getIsRobot();
  const isHomepageRedesignV2 = useIsLoggedOutHomepageV2();

  const showLocalizedContent =
    (isRobot && (localeCountry === "FRA" || localeCountry === "GBR")) ||
    (!isRobot && (countryCode === "FRA" || countryCode === "GBR"));

  const currentVariant = useGetTopHeaderBannerVariant({ showLocalizedContent });
  logAssert(!!currentVariant, "currentVariant is hardcoded, cannot be null");

  const { backgroundColor, responsiveImage, variantId, name, location } =
    currentVariant;
  const ref = useDoActionWhenVisible(() => {
    trackLoggedOutRetailersLoggedOutHomeRedesignView(
      variantId ?? "",
      isHomepageRedesignV2 ? "v2" : "v1"
    );
  });
  const { isDesktopAndAbove } = useViewport();

  return (
    <div ref={loadTimingRef}>
      <SlideIn offset={getSpacing("3x")}>
        <Flex
          className="m-4 h-fit md:m-6"
          direction={{
            mobileAndAbove: "column-reverse",
            desktopAndAbove: "row",
          }}
          style={{
            backgroundColor: backgroundColor,
            position: "relative",
          }}
          data-test-id="top-header-banner"
        >
          <div
            ref={ref}
            className="w-full text-center lg:w-1/2 lg:text-left"
            style={{
              height: "100%",
              alignItems: "center",
              display: "flex",
              position: "relative",
            }}
          >
            <LeftContent />
          </div>

          <div className="relative w-full lg:w-1/2">
            <LoggedOutHomeImage
              responsiveImage={responsiveImage}
              optimizeImageOptions={
                showLocalizedContent
                  ? {
                      mobileAndAbove: {
                        width: 355,
                        height: 150,
                      },
                      tabletAndAbove: {
                        width: 704,
                        height: 532,
                      },
                      desktopAndAbove: {
                        width: 420,
                        height: 262,
                      },
                      xLargeAndAbove: {
                        width: 698,
                        height: 316,
                      },
                      xxLargeDesktop: {
                        width: 842,
                        height: 372,
                      },
                    }
                  : {
                      mobileAndAbove: {
                        width: MOBILE_BANNER_WIDTH,
                        height: MOBILE_BANNER_HEIGHT,
                      },
                      tabletAndAbove: {
                        width: TABLET_BANNER_WIDTH,
                        height: TABLET_BANNER_HEIGHT,
                      },
                      desktopAndAbove: {
                        width: DESKTOP_BANNER_WIDTH,
                        height: DESKTOP_BANNER_HEIGHT,
                      },
                      xLargeAndAbove: {
                        width: XL_DESKTOP_BANNER_WIDTH,
                        height: XL_DESKTOP_BANNER_HEIGHT,
                      },
                      xxLargeDesktop: {
                        width: XXL_DESKTOP_BANNER_WIDTH,
                        height: XL_DESKTOP_BANNER_HEIGHT,
                      },
                    }
              }
              imageStyles={
                isDesktopAndAbove
                  ? {
                      display: "inline",
                      height: 0,
                      minHeight: "100%",
                      objectPosition: "center 25%", // Ensures header image is centered and not cut off
                    }
                  : {}
              }
            />

            <Credit name={name} location={location} />
          </div>
        </Flex>
      </SlideIn>
    </div>
  );
};

const LeftContent = () => {
  const { strictLocalize } = useStrictLocalization();
  const headerText = strictLocalize({
    defaultMessage: "Buy products for your store your customers will love.",
    description: {
      text: "Logged out home page top header banner title",
      img: "https://cdn.faire.com/fastly/198b7327d7b3b1bf10c635114a0a85954d5953837928efd1b95c27857aec4a6d.png",
    },
  });
  const history = useHistory();

  return (
    <Column
      align={{ mobileAndAbove: "center", desktopAndAbove: "flex-start" }}
      className={cn(
        "mx-auto",
        "px-4 py-10",
        "md:px-20 md:py-16",
        "lg:p-14",
        "xl:px-28 xl:py-20",
        "2xl:px-[168px] 2xl:py-28"
      )}
    >
      <Typography
        as="h1"
        variant={{
          mobileAndAbove: "displayMSerifRegular",
          tabletAndAbove: "displayLSerifRegular",
        }}
        color={Core.text.inverse}
      >
        {headerText}
      </Typography>
      <Spacer height="4x" />
      <Button
        style={{ width: "fit-content", border: "none" }}
        variant="secondary"
        onClick={() =>
          popSignUp(history, SignUpType.GENERIC, () => null, {
            element: SignUpElement.TOP_BANNER,
            sourceId: "logged_out_homepage_top_header_banner",
          })
        }
      >
        <StrictLocalMsg
          defaultMessage="Sign up to buy"
          description={{
            text: "Logged out home page top header banner cta button text",
          }}
        />
      </Button>
    </Column>
  );
};

const Credit = ({ name, location }: { name: string; location: string }) => {
  return (
    <ResponsiveContentV2
      desktopAndAbove={
        <div className="[text-shadow:1px_1px_4px_rgba(0,0,0,0.25) absolute right-8 bottom-8 flex flex-col text-right">
          <Typography variant="paragraphSansMedium" color={Core.text.inverse}>
            {name}
          </Typography>
          <Typography color={Core.text.inverse}>{location}</Typography>
        </div>
      }
    />
  );
};
